import { memo, useCallback, useMemo, useState } from 'react'
import StakingTable from './StakingTable'
import ReferralsTable from './ReferralsTable'
import NavigationButtonGroup from '@components/NavigationButtonGroup'
import { useAsync } from 'react-async-hook'
import { ListingV0, RefereRecipient } from './HoldingDetails.type'
import { PublicKey } from '@metaplex-foundation/js'
import { isAfter, isBefore } from 'date-fns'
import { bs58 } from '@coral-xyz/anchor/dist/cjs/utils/bytes'
import { useSolana } from '@dewicats/connect-button'
import { Holding } from '@hooks/useHoldings'
import useStaking from '@hooks/useStaking'
import { init as initAuctionManager } from '@dewicats/auction-manager-sdk'

const TABS = ['Staking Rewards', 'Referrals Rewards'] as const

type RewardsSectionProps = {
  holding: Holding
}

const RewardsSection = ({ holding }: RewardsSectionProps) => {
  const [activeRoute, setActiveRoute] = useState<string>(TABS[0])
  const { anchorProvider } = useSolana()
  const { result: auctionProgram } = useAsync(() => {
    return initAuctionManager(anchorProvider)
  }, [anchorProvider])

  const { stakeMetadataAcc } = useStaking({
    nft: holding.publicKey,
  })

  const stakedAt = useMemo(() => {
    if (stakeMetadataAcc?.stakedAt === null) return null
    return new Date(stakeMetadataAcc?.stakedAt?.toNumber() * 1000)
  }, [stakeMetadataAcc])

  const unstakedAt = useMemo(() => {
    if (stakeMetadataAcc?.unstakedAt === null) return null
    return new Date(stakeMetadataAcc?.unstakedAt?.toNumber() * 1000)
  }, [stakeMetadataAcc])

  const getStakedListings = useCallback(async () => {
    if (auctionProgram) {
      const listings = (
        await auctionProgram.account.listingV0.all([
          {
            memcmp: {
              offset:
                32 +
                32 +
                32 +
                8 +
                8 +
                8 +
                32 +
                8 +
                32 +
                8 +
                1 +
                32 +
                8 +
                8 +
                8 +
                8,
              bytes: bs58.encode([0x01]),
            },
          },
        ])
      ).map((i) => {
        return {
          ...i.account,
          publicKey: i.publicKey,
        }
      })

      // TODO: Show stake reciepts instead of listings if unstaked is true

      if (!stakedAt || stakedAt === null) {
        // If staked at is null, we show all listings that come before unstakedAt
        const listingsBeforeUnstakedAt = listings.filter((listing) => {
          return (
            listing.state.sold &&
            !listing.state.active &&
            isBefore(new Date(listing.createdAt.toNumber() * 1000), unstakedAt)
          )
        })

        return listingsBeforeUnstakedAt
      }

      // // only show listing that were created at least 24 hours after the NFT was staked
      const listingsAfterStakedAt = listings.filter((listing) => {
        return (
          listing.state.sold &&
          !listing.state.active &&
          isAfter(new Date(listing.createdAt.toNumber() * 1000), stakedAt) &&
          listing.rewardPercentage.toNumber() > 0
        )
      })

      return listingsAfterStakedAt
    }
  }, [auctionProgram, stakedAt, unstakedAt])

  const getReferralRecipients = useCallback(
    async (nft: PublicKey): Promise<RefereRecipient[]> => {
      if (auctionProgram) {
        const referrals = await auctionProgram.account.referralRecipientV0.all([
          {
            memcmp: {
              offset: 8,
              bytes: nft.toBase58(),
            },
          },
        ])

        const referralsWithListings = await Promise.all(
          referrals.map(async (i) => {
            const listingAcc: ListingV0 =
              await auctionProgram.account.listingV0.fetch(i.account.listing)

            return {
              ...i.account,
              listingAcc,
              publicKey: i.publicKey,
            } as RefereRecipient
          }),
        )

        return referralsWithListings
      }
    },
    [auctionProgram],
  )

  const { result: referralRecipients } = useAsync(async () => {
    return await getReferralRecipients(holding.publicKey)
  }, [holding, getReferralRecipients])

  const { result: stakedListings, execute } = useAsync(async () => {
    return await getStakedListings()
  }, [getStakedListings])

  const onSelect = (route: string) => {
    setActiveRoute(route)
  }

  const handleClaimAll = useCallback(() => {
    execute()
  }, [execute])

  const navRoutes = useMemo(() => {
    return TABS.map((tab) => ({
      name: tab,
      isNav: false,
    }))
  }, [])

  return (
    <div className="w-full">
      <div className="mt-8">
        <NavigationButtonGroup
          routes={navRoutes}
          onClick={onSelect}
          selected={activeRoute}
        />
      </div>
      {activeRoute === TABS[1] && (
        <ReferralsTable
          referralRecipients={referralRecipients}
          holding={holding}
        />
      )}

      {activeRoute === TABS[0] && (
        <StakingTable
          stakedListings={stakedListings}
          holding={holding}
          onClaimAll={handleClaimAll}
        />
      )}
    </div>
  )
}

export default memo(RewardsSection)
